import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Tabs, Tab, Card, Badge, Table, Form, Button, Row, Col } from "react-bootstrap";
import NavBar from "../../../../../components/NavBarAdmin";
import { toast } from "react-toastify";
import { Puff } from "react-loading-icons";
import { Grid } from "../../../../Layout/Grid/GridView";

// API requests
import VenueEventDetailsRequests from "./VenueEventDetailsRequests";
import EventGuestlistStatsRequests from "../../../Events/list/guestlistStats/EventGuestlistStatsRequests";

// Stats views
import GuestlistStatsView from "../../../Events/list/guestlistStats/listEntries/views/GuestlistStatsView";
import PromotersStatsView from "../../../Events/list/guestlistStats/listEntries/views/PromotersStatsView";
import TableStatsView from "../../../Events/list/guestlistStats/listEntries/views/TableStatsView";
import TicketsStatsView from "../../../Events/list/guestlistStats/listEntries/views/TicketsStatsView";
import FinancialView from "../../../Events/list/guestlistStats/listEntries/views/FinancialView";

const VenueEventDetails = () => {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("geral");
  const [activeStatsTab, setActiveStatsTab] = useState("guestlist");
  
  // Data state
  const [eventForm, setEventForm] = useState({
    id: 0,
    name: "",
    id_venues: "",
    description: "",
    date: "",
    date_end: "",
    timezone: "",
    music_genre: [],
    event_image_url: "",
    status: "",
    location: { formatted_address: "" },
    guestlist: { enable: false, capacity: "", visibility: "PUBLIC" },
    tables: { enable: false, list: [] },
    tickets: { enable: false, list: [] }
  });
  
  const [venueInfo, setVenueInfo] = useState({ name: "" });
  const [ticketsTransactions, setTicketsTransactions] = useState([]);
  const [tablesTransactions, setTablesTransactions] = useState([]);
  const [statsData, setStatsData] = useState({
    guestlist: null,
    promoters: null,
    tables: null,
    tickets: null,
    financial: null
  });
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  // Administrative form states
  const [postponeDate, setPostponeDate] = useState("");
  const [postponeMessage, setPostponeMessage] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [alertSubject, setAlertSubject] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [refundTxNumber, setRefundTxNumber] = useState("");
  const [confirmRefundAll, setConfirmRefundAll] = useState("");

  const gridTicketsRef = useRef();

  const fetchEventData = async () => {
    setIsLoading(true);
    try {
      // Get Event Details & Configuration
      const detailsRes = await VenueEventDetailsRequests.getEventDetails({
        id: params.id_events,
        id_venues: params.id
      });
      if (detailsRes && detailsRes.status) {
        setEventForm(detailsRes.data.form || detailsRes.data);
        if (detailsRes.data.generalInfo?.venues) {
          const matchedVenue = detailsRes.data.generalInfo.venues.find(
            (v) => v.id.toString() === params.id.toString()
          );
          if (matchedVenue) setVenueInfo(matchedVenue);
        }
      }

      // Get Ticket Transactions
      const ticketsTxRes = await VenueEventDetailsRequests.getTicketsTransactions({
        id_events: params.id_events
      });
      if (ticketsTxRes && ticketsTxRes.status) {
        setTicketsTransactions(ticketsTxRes.data.transactions || []);
      }

      // Get Table Transactions
      const tablesTxRes = await VenueEventDetailsRequests.getTablesTransactions({
        id_events: params.id_events
      });
      if (tablesTxRes && tablesTxRes.status) {
        setTablesTransactions(tablesTxRes.data.transactions || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar detalhes do evento.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async (tabName) => {
    setIsStatsLoading(true);
    try {
      const statsRes = await EventGuestlistStatsRequests.getEventGuestlistStatsEntries({
        id: params.id_events,
        tab: tabName
      });
      if (statsRes && statsRes.status) {
        setStatsData(statsRes.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar estatísticas.");
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Detalhes do Evento | Nightspot Dashboard";
    fetchEventData();
  }, [params.id_events]);

  useEffect(() => {
    if (activeTab === "estatisticas") {
      fetchStats(activeStatsTab);
    }
  }, [activeTab, activeStatsTab]);

  // Billing and Commission Computations
  // Commision is usually calculated per transaction. 
  // Let's sum up completed tickets & tables payments and calculate commission.
  const billingCalculations = () => {
    const COMPLETED_STATUS = "PAID";
    
    // Filter completed transactions
    const paidTickets = ticketsTransactions.filter(
      (t) => t.payment_status?.toUpperCase() === COMPLETED_STATUS
    );
    const paidTables = tablesTransactions.filter(
      (t) => t.status?.toUpperCase() === COMPLETED_STATUS || t.Estado?.toUpperCase() === "PAGO" || t.Estado?.toUpperCase() === "COMPLETED"
    );

    // Sum totals
    let ticketGross = 0;
    let ticketCommissions = 0;
    let ticketFixedFees = 0;
    
    paidTickets.forEach((t) => {
      const price = parseFloat(t.transaction_price_total) || 0;
      const ticketVal = parseFloat(t.transaction_price_ticket) || 0;
      const fee = parseFloat(t.transaction_price_fee) || 0;
      const fixedFee = parseFloat(t.transaction_price_fee_fixed) || 0;
      ticketGross += price;
      ticketCommissions += fee;
      ticketFixedFees += fixedFee;
    });

    let tableGross = 0;
    let tableCommissions = 0; // if there is any standard platform commission on tables
    paidTables.forEach((t) => {
      const price = parseFloat(t.Valor) || parseFloat(t.price) || 0;
      tableGross += price;
      // standard tables commission (e.g. 5% if not defined, or check if object has a fee property)
      const commissionVal = parseFloat(t.fee) || (price * 0.05);
      tableCommissions += commissionVal;
    });

    const totalGross = ticketGross + tableGross;
    const totalCommissions = ticketCommissions + tableCommissions;
    const totalFixedFees = ticketFixedFees;
    const netPayout = totalGross - totalCommissions - totalFixedFees;

    return {
      totalGross,
      totalCommissions,
      totalFixedFees,
      netPayout,
      paidTicketsCount: paidTickets.length,
      paidTablesCount: paidTables.length
    };
  };

  const billing = billingCalculations();

  // Administrative Actions
  const handlePostpone = async (e) => {
    e.preventDefault();
    if (!postponeDate) {
      toast.warning("Selecione a nova data do evento.");
      return;
    }
    const res = await VenueEventDetailsRequests.postponeEvent({
      id_events: params.id_events,
      new_date: postponeDate,
      message: postponeMessage
    });
    if (res && res.status) {
      toast.success("Evento adiado e clientes notificados.");
      setPostponeDate("");
      setPostponeMessage("");
      fetchEventData();
    } else {
      toast.error(res?.msg || "Erro ao adiar evento.");
    }
  };

  const handleCancel = async (e) => {
    e.preventDefault();
    if (!window.confirm("Tem a certeza que deseja cancelar este evento? Esta ação não pode ser desfeita.")) {
      return;
    }
    const res = await VenueEventDetailsRequests.cancelEvent({
      id_events: params.id_events,
      message: cancelMessage
    });
    if (res && res.status) {
      toast.success("Evento cancelado com sucesso e clientes notificados.");
      setCancelMessage("");
      fetchEventData();
    } else {
      toast.error(res?.msg || "Erro ao cancelar evento.");
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!alertSubject || !alertMessage) {
      toast.warning("Preencha o assunto e a mensagem do aviso.");
      return;
    }
    const res = await VenueEventDetailsRequests.sendGeneralNotification({
      id_events: params.id_events,
      subject: alertSubject,
      message: alertMessage
    });
    if (res && res.status) {
      toast.success("Comunicado enviado para todos os compradores.");
      setAlertSubject("");
      setAlertMessage("");
    } else {
      toast.error(res?.msg || "Erro ao enviar comunicado.");
    }
  };

  const handleSingleRefund = async (e) => {
    e.preventDefault();
    if (!refundTxNumber) {
      toast.warning("Insira o número da transação.");
      return;
    }
    if (!window.confirm(`Tem a certeza que deseja reembolsar a transação ${refundTxNumber}?`)) {
      return;
    }
    const res = await VenueEventDetailsRequests.refundSingleTransaction({
      id_transaction: refundTxNumber,
      id_events: params.id_events
    });
    if (res && res.status) {
      toast.success("Reembolso efetuado com sucesso.");
      setRefundTxNumber("");
      fetchEventData();
    } else {
      toast.error(res?.msg || "Erro ao proceder ao reembolso.");
    }
  };

  const handleRefundAll = async (e) => {
    e.preventDefault();
    if (confirmRefundAll !== eventForm.name) {
      toast.warning("Confirmação incorreta. Escreva o nome do evento corretamente.");
      return;
    }
    if (!window.confirm(`ATENÇÃO CRÍTICA: Vai reembolsar TODOS os bilhetes e reservas efetuados para o evento "${eventForm.name}". Deseja mesmo continuar?`)) {
      return;
    }
    const res = await VenueEventDetailsRequests.refundAllTransactions({
      id_events: params.id_events
    });
    if (res && res.status) {
      toast.success("Todos os reembolsos foram agendados/processados com sucesso.");
      setConfirmRefundAll("");
      fetchEventData();
    } else {
      toast.error(res?.msg || "Erro ao processar reembolsos em massa.");
    }
  };

  // Render sub-components
  const InsideLoading = () => (
    <div className="d-flex flex-column align-items-center justify-content-center py-5">
      <Puff stroke="#f92056" height="5em" width="5em" />
      <div className="mt-3 text-muted">A carregar detalhes do evento...</div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    let variant = "secondary";
    let text = status || "Desconhecido";
    if (status === "OPEN" || status === "ACTIVE" || status === "APPROVED") {
      variant = "success";
      text = "Ativo";
    } else if (status === "CLOSED" || status === "INACTIVE" || status === "CANCELLED") {
      variant = "danger";
      text = "Cancelado/Fechado";
    } else if (status === "PENDING") {
      variant = "warning";
      text = "Pendente";
    }
    return <Badge bg={variant} className="fs-6 px-3 py-2">{text}</Badge>;
  };

  // Columns for Ticket Transactions Grid
  const ticketTxCols = [
    {
      headerName: "Ações",
      cellRenderer: (props) => (
        <div className="text-center">
          <Link
            to={`/my/admin/venues/${params.id}/events/${params.id_events}/transactions/${props.data.id}`}
            className="btn btn-xs btn-primary me-2"
            style={{ padding: "2px 8px", fontSize: "11px" }}
          >
            Detalhes
          </Link>
          {props.data.payment_status?.toUpperCase() === "PAID" && (
            <button
              className="btn btn-xs btn-danger"
              style={{ padding: "2px 8px", fontSize: "11px" }}
              onClick={async () => {
                if (window.confirm(`Deseja reembolsar esta transação de ${props.data.client_name}?`)) {
                  const res = await VenueEventDetailsRequests.refundSingleTransaction({
                    id_transaction: props.data.id,
                    id_events: params.id_events
                  });
                  if (res && res.status) {
                    toast.success("Transação reembolsada.");
                    fetchEventData();
                  } else {
                    toast.error(res?.msg || "Erro ao reembolsar.");
                  }
                }
              }}
            >
              Reembolsar
            </button>
          )}
        </div>
      )
    },
    { headerName: "Data", field: "transaction_date" },
    { headerName: "Cliente", field: "client_name" },
    { headerName: "Email", field: "owner_email" },
    {
      headerName: "Telefone",
      cellRenderer: (p) => `${p.data.client_phone_country || ""} ${p.data.client_phone_number || ""}`
    },
    { headerName: "Valor (€)", field: "transaction_price_total", cellStyle: { textAlign: "right" } },
    { headerName: "Estado", field: "payment_status" },
    { headerName: "Nº Transação", field: "transaction_number" }
  ];

  return (
    <div>
      <NavBar>
        <div className="row">
          <div className="col-xs-12 col-sm-12 dashboard-content">
            <div className="row mb-2">
              <div className="col-12">
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/my/admin/dashboard">
                        <i className="fa-solid fa-home"></i>
                      </Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/my/admin/venues">Venues</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={`/my/admin/venues/${params.id}/events`}>Eventos</Link>
                    </li>
                    <li className="breadcrumb-item active">Detalhes do Evento</li>
                  </ol>
                </nav>
              </div>
            </div>

            {isLoading ? (
              <InsideLoading />
            ) : (
              <>
                <div className="row align-items-center mb-4">
                  <div className="col-md-8">
                    <h2 className="dashboard-h2 m-0 d-flex align-items-center gap-3">
                      {eventForm.name}
                      <StatusBadge status={eventForm.status} />
                    </h2>
                    <p className="text-muted m-0 mt-1">
                      <i className="fa-solid fa-hotel me-1"></i> {venueInfo.name || "Venue"}
                    </p>
                  </div>
                  <div className="col-md-4 text-md-end mt-2 mt-md-0">
                    <Link
                      to={`/my/admin/venues/${params.id}/events`}
                      className="btn btn-outline-secondary"
                    >
                      <i className="fa-solid fa-arrow-left"></i> Voltar à lista
                    </Link>
                  </div>
                </div>

                <div className="card shadow-sm border-0 bg-dark text-white p-3">
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="tab-nested mb-4"
                    justify
                  >
                    <Tab eventKey="geral" title="Geral">
                      <Row className="g-4">
                        <Col md={4} className="text-center">
                          {eventForm.event_image_url ? (
                            <img
                              src={eventForm.event_image_url}
                              alt={eventForm.name}
                              className="img-fluid rounded shadow-sm border border-secondary"
                              style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center rounded bg-secondary text-light shadow-sm"
                              style={{ height: "250px" }}
                            >
                              Sem Imagem
                            </div>
                          )}
                        </Col>
                        <Col md={8}>
                          <Card className="bg-transparent border-0 text-white">
                            <Card.Body className="p-0">
                              <h4 className="border-bottom border-secondary pb-2 mb-3">Informações Gerais</h4>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Data Início:</Col>
                                <Col sm={9}>{eventForm.date || "N/A"}</Col>
                              </Row>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Data Fim:</Col>
                                <Col sm={9}>{eventForm.date_end || eventForm.date || "N/A"}</Col>
                              </Row>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Timezone:</Col>
                                <Col sm={9}>{eventForm.timezone || "N/A"}</Col>
                              </Row>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Localização:</Col>
                                <Col sm={9}>{eventForm.location?.formatted_address || "N/A"}</Col>
                              </Row>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Estilo de Música:</Col>
                                <Col sm={9}>
                                  {eventForm.music_genre && eventForm.music_genre.length > 0
                                    ? eventForm.music_genre.map((genre, idx) => (
                                        <Badge bg="info" className="me-1" key={idx}>
                                          {genre}
                                        </Badge>
                                      ))
                                    : "N/A"}
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col sm={3} className="text-muted font-weight-bold">Descrição:</Col>
                                <Col sm={9} style={{ whiteSpace: "pre-line" }}>
                                  {eventForm.description || "Sem descrição disponível."}
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab>

                    <Tab eventKey="configuracoes" title="Configurações">
                      <Row className="g-4">
                        <Col lg={4}>
                          <Card className="h-100 bg-secondary text-white border-0 shadow-sm">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-list-check me-2"></i> Guestlist
                              </h5>
                              <Row className="mb-2">
                                <Col xs={6} className="text-muted">Estado:</Col>
                                <Col xs={6}>
                                  {eventForm.guestlist?.enable ? (
                                    <Badge bg="success">Ativo</Badge>
                                  ) : (
                                    <Badge bg="secondary">Inativo</Badge>
                                  )}
                                </Col>
                              </Row>
                              {eventForm.guestlist?.enable && (
                                <>
                                  <Row className="mb-2">
                                    <Col xs={6} className="text-muted">Capacidade:</Col>
                                    <Col xs={6}>{eventForm.guestlist.capacity || "Ilimitada"}</Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col xs={6} className="text-muted">Visibilidade:</Col>
                                    <Col xs={6}>{eventForm.guestlist.visibility}</Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col xs={6} className="text-muted">Fechar no App:</Col>
                                    <Col xs={6}>
                                      {eventForm.guestlist.allowAppGuestList ? "Não" : "Sim"}
                                    </Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col xs={6} className="text-muted">Enviar SMS:</Col>
                                    <Col xs={6}>{eventForm.guestlist.sendSmsInvite ? "Sim" : "Não"}</Col>
                                  </Row>
                                </>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={4}>
                          <Card className="h-100 bg-secondary text-white border-0 shadow-sm">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-chair me-2"></i> VIP Mesas
                              </h5>
                              <Row className="mb-3">
                                <Col xs={6} className="text-muted">Estado:</Col>
                                <Col xs={6}>
                                  {eventForm.tables?.enable ? (
                                    <Badge bg="success">Ativo</Badge>
                                  ) : (
                                    <Badge bg="secondary">Inativo</Badge>
                                  )}
                                </Col>
                              </Row>
                              {eventForm.tables?.enable && eventForm.tables.list && (
                                <Table striped bordered hover variant="dark" size="sm">
                                  <thead>
                                    <tr>
                                      <th>Mesa</th>
                                      <th>Cap.</th>
                                      <th>Consumo Mín.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {eventForm.tables.list.map((table, idx) => (
                                      <tr key={idx}>
                                        <td>{table.name}</td>
                                        <td>{table.capacity} pax</td>
                                        <td>{table.minimum_consumption}€</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={4}>
                          <Card className="h-100 bg-secondary text-white border-0 shadow-sm">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-ticket me-2"></i> Bilhetes
                              </h5>
                              <Row className="mb-3">
                                <Col xs={6} className="text-muted">Estado:</Col>
                                <Col xs={6}>
                                  {eventForm.tickets?.enable ? (
                                    <Badge bg="success">Ativo</Badge>
                                  ) : (
                                    <Badge bg="secondary">Inativo</Badge>
                                  )}
                                </Col>
                              </Row>
                              {eventForm.tickets?.enable && eventForm.tickets.list && (
                                <Table striped bordered hover variant="dark" size="sm">
                                  <thead>
                                    <tr>
                                      <th>Nome</th>
                                      <th>Preço</th>
                                      <th>Qtd.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {eventForm.tickets.list.map((ticket, idx) => (
                                      <tr key={idx}>
                                        <td>{ticket.name}</td>
                                        <td>{ticket.price}€</td>
                                        <td>{ticket.capacity || "Ilimitado"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab>

                    <Tab eventKey="transacoes" title="Transações">
                      <Tabs defaultActiveKey="tickets-tx" id="sub-transacoes" className="mb-3">
                        <Tab eventKey="tickets-tx" title={`Bilhetes (${ticketsTransactions.length})`}>
                          <div style={{ minHeight: "400px" }}>
                            <Grid
                              ref={gridTicketsRef}
                              rows={ticketsTransactions}
                              cols={ticketTxCols}
                              height="50vh"
                            />
                          </div>
                        </Tab>
                        <Tab eventKey="tables-tx" title={`Mesas (${tablesTransactions.length})`}>
                          <Table striped bordered hover variant="dark" responsive>
                            <thead>
                              <tr>
                                <th>Data Reserva</th>
                                <th>Cliente</th>
                                <th>Contacto</th>
                                <th>Mesa / Área</th>
                                <th>Valor Pago (€)</th>
                                <th>Método Pagamento</th>
                                <th>Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tablesTransactions.length > 0 ? (
                                tablesTransactions.map((tx, idx) => (
                                  <tr key={idx}>
                                    <td>{tx.date || tx.transaction_date || "N/A"}</td>
                                    <td>{tx.client_name || "N/A"}</td>
                                    <td>{tx.client_phone_country} {tx.client_phone_number}</td>
                                    <td>{tx.table_name || "N/A"}</td>
                                    <td className="text-end">{(tx.Valor || tx.price || 0)}€</td>
                                    <td>
                                      {tx.payment_method ? (
                                        <div className="text-center">
                                          <img
                                            src={`/img/payments/${tx.payment_method}.png`}
                                            alt={tx.payment_method}
                                            style={{ height: "20px" }}
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />{" "}
                                          <span className="ms-1 small">{tx.payment_method}</span>
                                        </div>
                                      ) : (
                                        "N/A"
                                      )}
                                    </td>
                                    <td>
                                      <Badge
                                        bg={
                                          tx.Estado?.toUpperCase() === "PAGO" ||
                                          tx.Estado?.toUpperCase() === "COMPLETED" ||
                                          tx.status?.toUpperCase() === "PAID"
                                            ? "success"
                                            : "warning"
                                        }
                                      >
                                        {tx.Estado || tx.status || "N/A"}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="text-center text-muted">
                                    Sem transações de mesas encontradas.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </Tab>
                      </Tabs>
                    </Tab>

                    <Tab eventKey="estatisticas" title="Estatísticas">
                      <Tabs
                        activeKey={activeStatsTab}
                        onSelect={(k) => setActiveStatsTab(k)}
                        className="tab-nested mb-3"
                        justify
                      >
                        <Tab eventKey="guestlist" title="Guest List">
                          {isStatsLoading ? <InsideLoading /> : <GuestlistStatsView data={statsData.guestlist} />}
                        </Tab>
                        <Tab eventKey="promoters" title="Promotores">
                          {isStatsLoading ? <InsideLoading /> : <PromotersStatsView data={statsData.promoters} />}
                        </Tab>
                        <Tab eventKey="tables" title="Mesas">
                          {isStatsLoading ? <InsideLoading /> : <TableStatsView data={statsData.tables} />}
                        </Tab>
                        <Tab eventKey="tickets" title="Bilhetes">
                          {isStatsLoading ? <InsideLoading /> : <TicketsStatsView data={statsData.tickets} />}
                        </Tab>
                        <Tab eventKey="financial" title="Financeiro">
                          {isStatsLoading ? <InsideLoading /> : <FinancialView data={statsData.financial} />}
                        </Tab>
                      </Tabs>
                    </Tab>

                    <Tab eventKey="faturacao" title="Faturação & Comissões">
                      <Row className="g-4 mb-4">
                        <Col md={3}>
                          <Card className="bg-secondary text-white border-0 shadow-sm text-center py-3">
                            <Card.Body className="p-2">
                              <h6 className="text-muted text-uppercase mb-2 small">Faturação Bruta Total</h6>
                              <h2 className="m-0 text-info">{billing.totalGross.toFixed(2)}€</h2>
                              <p className="text-muted m-0 mt-2 small">
                                {billing.paidTicketsCount} bilhetes + {billing.paidTablesCount} mesas pagos
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col md={3}>
                          <Card className="bg-secondary text-white border-0 shadow-sm text-center py-3">
                            <Card.Body className="p-2">
                              <h6 className="text-muted text-uppercase mb-2 small">Comissões Plataforma</h6>
                              <h2 className="m-0 text-warning">{billing.totalCommissions.toFixed(2)}€</h2>
                              <p className="text-muted m-0 mt-2 small">Calculado s/ transações bem-sucedidas</p>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={3}>
                          <Card className="bg-secondary text-white border-0 shadow-sm text-center py-3">
                            <Card.Body className="p-2">
                              <h6 className="text-muted text-uppercase mb-2 small">Taxas Fixas Processamento</h6>
                              <h2 className="m-0 text-muted">{billing.totalFixedFees.toFixed(2)}€</h2>
                              <p className="text-muted m-0 mt-2 small">Cobrada pela operadora de pagamentos</p>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={3}>
                          <Card className="bg-secondary text-white border-0 shadow-sm text-center py-3">
                            <Card.Body className="p-2">
                              <h6 className="text-muted text-uppercase mb-2 small">Líquido a Transferir</h6>
                              <h2 className="m-0 text-success">{billing.netPayout.toFixed(2)}€</h2>
                              <p className="text-muted m-0 mt-2 small">Montante devido ao promotor</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>


                      <h5 className="mt-4 mb-3 border-bottom border-secondary pb-2">Detalhamento de Comissão por Transação</h5>
                      <Table striped bordered hover variant="dark" responsive>
                        <thead>
                          <tr>
                            <th>Nº Transação</th>
                            <th>Cliente</th>
                            <th>Tipo</th>
                            <th>Valor Bruto (€)</th>
                            <th>Comissão Aplicada (€)</th>
                            <th>Taxa Fixa (€)</th>
                            <th>Valor Líquido (€)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticketsTransactions.filter(t => t.payment_status?.toUpperCase() === "PAID").map((tx, idx) => {
                            const gross = parseFloat(tx.transaction_price_total) || 0;
                            const comm = parseFloat(tx.transaction_price_fee) || 0;
                            const fixed = parseFloat(tx.transaction_price_fee_fixed) || 0;
                            const net = gross - comm - fixed;
                            return (
                              <tr key={`t-${idx}`}>
                                <td>{tx.transaction_number || "N/A"}</td>
                                <td>{tx.client_name || "N/A"}</td>
                                <td><Badge bg="primary">Bilhete</Badge></td>
                                <td className="text-end">{gross.toFixed(2)}€</td>
                                <td className="text-end text-warning">-{comm.toFixed(2)}€</td>
                                <td className="text-end text-muted">-{fixed.toFixed(2)}€</td>
                                <td className="text-end text-success">{net.toFixed(2)}€</td>
                              </tr>
                            );
                          })}
                          {tablesTransactions.filter(t => t.status?.toUpperCase() === "PAID" || t.Estado?.toUpperCase() === "PAGO").map((tx, idx) => {
                            const gross = parseFloat(tx.Valor || tx.price) || 0;
                            const comm = parseFloat(tx.fee) || (gross * 0.05);
                            const net = gross - comm;
                            return (
                              <tr key={`tab-${idx}`}>
                                <td>{tx.id || "N/A"}</td>
                                <td>{tx.client_name || "N/A"}</td>
                                <td><Badge bg="info">Mesa VIP</Badge></td>
                                <td className="text-end">{gross.toFixed(2)}€</td>
                                <td className="text-end text-warning">-{comm.toFixed(2)}€</td>
                                <td className="text-end">0.00€</td>
                                <td className="text-end text-success">{net.toFixed(2)}€</td>
                              </tr>
                            );
                          })}
                          {(ticketsTransactions.filter(t => t.payment_status?.toUpperCase() === "PAID").length === 0 &&
                            tablesTransactions.filter(t => t.status?.toUpperCase() === "PAID" || t.Estado?.toUpperCase() === "PAGO").length === 0) && (
                            <tr>
                              <td colSpan="7" className="text-center text-muted py-3">
                                Nenhuma transação paga disponível para faturamento.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Tab>

                    <Tab eventKey="administracao" title="Área Administrativa">
                      <Row className="g-4">
                        <Col lg={6}>
                          <Card className="bg-secondary text-white border-0 shadow-sm mb-4">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-clock-rotate-left me-2 text-warning"></i> Adiar Evento
                              </h5>
                              <Form onSubmit={handlePostpone}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Nova Data e Hora do Evento</Form.Label>
                                  <Form.Control
                                    type="datetime-local"
                                    value={postponeDate}
                                    onChange={(e) => setPostponeDate(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Mensagem de Aviso (Email/SMS)</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Indique o motivo e outros detalhes para os clientes..."
                                    value={postponeMessage}
                                    onChange={(e) => setPostponeMessage(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Button type="submit" variant="warning" className="w-100">
                                  Anunciar Adiamento e Notificar Clientes
                                </Button>
                              </Form>
                            </Card.Body>
                          </Card>

                          <Card className="bg-secondary text-white border-0 shadow-sm">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-bullhorn me-2 text-info"></i> Comunicado Geral
                              </h5>
                              <Form onSubmit={handleSendNotification}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Assunto do Comunicado</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Ex: Informação importante sobre parque de estacionamento..."
                                    value={alertSubject}
                                    onChange={(e) => setAlertSubject(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Mensagem</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Escreva a mensagem a enviar aos compradores..."
                                    value={alertMessage}
                                    onChange={(e) => setAlertMessage(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Button type="submit" variant="info" className="w-100">
                                  Enviar Mensagem Geral
                                </Button>
                              </Form>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={6}>
                          <Card className="bg-secondary text-white border-0 shadow-sm mb-4">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3">
                                <i className="fa-solid fa-rotate-left me-2 text-danger"></i> Devolver Transação Individual
                              </h5>
                              <Form onSubmit={handleSingleRefund}>
                                <Form.Group className="mb-3">
                                  <Form.Label>ID ou Código da Transação</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Ex: TX-4921938"
                                    value={refundTxNumber}
                                    onChange={(e) => setRefundTxNumber(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Button type="submit" variant="danger" className="w-100">
                                  Proceder ao Reembolso
                                </Button>
                              </Form>
                            </Card.Body>
                          </Card>

                          <Card className="bg-secondary text-white border-0 shadow-sm mb-4 border border-danger">
                            <Card.Body>
                              <h5 className="border-bottom border-dark pb-2 mb-3 text-danger">
                                <i className="fa-solid fa-rectangle-xmark me-2 text-danger"></i> Cancelamento & Reembolso Total
                              </h5>
                              <Form onSubmit={handleCancel} className="mb-4">
                                <Form.Group className="mb-3">
                                  <Form.Label>Motivo do Cancelamento</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Explique o cancelamento aos compradores..."
                                    value={cancelMessage}
                                    onChange={(e) => setCancelMessage(e.target.value)}
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Button type="submit" variant="danger" className="w-100">
                                  Cancelar Evento
                                </Button>
                              </Form>

                              <h5 className="border-top border-dark pt-3 pb-2 mb-3 text-danger">
                                <i className="fa-solid fa-triangle-exclamation me-2"></i> Reembolsar Todos os Bilhetes
                              </h5>
                              <p className="text-muted small">
                                Esta ação irá proceder à devolução monetária automática de todas as transações finalizadas para este evento.
                              </p>
                              <Form onSubmit={handleRefundAll}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Para confirmar, escreva o nome do evento (<strong>{eventForm.name}</strong>):</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={confirmRefundAll}
                                    onChange={(e) => setConfirmRefundAll(e.target.value)}
                                    placeholder="Escreva o nome exato do evento"
                                    className="bg-dark text-white border-secondary"
                                  />
                                </Form.Group>
                                <Button
                                  type="submit"
                                  variant="danger"
                                  className="w-100"
                                  disabled={confirmRefundAll !== eventForm.name}
                                >
                                  Reembolsar Todos os Clientes
                                </Button>
                              </Form>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab>
                  </Tabs>
                </div>
              </>
            )}
          </div>
        </div>
      </NavBar>
    </div>
  );
};

export default VenueEventDetails;
